import { http } from '@pendo/http';
import { getCookie } from '@/utils/cookies.utils';

// Local requests use /predict-api for CORS and Origin rewriting; non-local requests use the sole dev host or shared prod host. Keep these hosts aligned with rsbuild.config.mts.
const PENDO_DEV_DOMAIN_SUFFIX = 'pendo-dev.pendo-dev.com';

function resolvePredictApiBase (): string {
    if (__PROCESS__.LOCAL_ENV) {
        return '/predict-api';
    }

    const host = typeof window !== 'undefined' ? window.location.hostname : '';

    if (host.endsWith(PENDO_DEV_DOMAIN_SUFFIX)) {
        return `//predict-api.${PENDO_DEV_DOMAIN_SUFFIX}`;
    }

    return '//predict-api.pendo.io';
}

export const PREDICT_API_BASE = resolvePredictApiBase();

// CSRF cookies set by the login response. The value must be echoed back on every
// mutating (POST) request as the X-CSRF-Token header. GET requests are exempt.
// Login sets two readable variants — the __Host- prefixed one (preferred) and a
// _c80 fallback for browsers that don't support the prefix.
const PREDICT_CSRF_COOKIES = ['__Host-predict.csrf', 'predict.csrf_c80'];
const X_CSRF_TOKEN = 'X-CSRF-Token';
const INVALID_CSRF_TOKEN_VALUES = new Set(['null', 'undefined']);
let cachedPredictCsrfToken: string | null = null;

// The session cookie (predict.pendo.jwt) rides along automatically via
// @pendo/http's withCredentials. We only need to forward the CSRF token, which
// we read from its cookie rather than persisting in app state.
function normalizePredictCsrfToken (token: unknown): string {
    const normalized = typeof token === 'string' ? token.trim() : '';

    if (!normalized || INVALID_CSRF_TOKEN_VALUES.has(normalized.toLowerCase())) {
        return '';
    }

    return normalized;
}

function resolvePredictCsrfToken (): string {
    const cookieToken = PREDICT_CSRF_COOKIES
        .map((name) => getCookie(document.cookie, name))
        .map(normalizePredictCsrfToken)
        .find((value) => value);

    if (cookieToken) {
        return cookieToken;
    }

    return normalizePredictCsrfToken(cachedPredictCsrfToken);
}

export class PredictCsrfTokenError extends Error {
    constructor () {
        super('Predict CSRF token is missing or invalid');
        this.name = 'PredictCsrfTokenError';
    }
}

export function predictCsrfHeaders (options: { requireToken?: boolean } = {}): Record<string, string> {
    const token = resolvePredictCsrfToken();

    if (!token && options.requireToken) {
        throw new PredictCsrfTokenError();
    }

    return token ? { [X_CSRF_TOKEN]: token } : {};
}

/* ------------------------------------------------------------------ */
/* Response envelope (gateway endpoints only)                          */
/* ------------------------------------------------------------------ */

export type CommonErrorCode = 'NOT_FOUND' | 'BAD_REQUEST' | 'VALIDATION_ERROR' | 'LOGIC_ERROR';

export interface ErrorContext {
    id: string | null;
    name: string | null;
    error: string | null;
    type: CommonErrorCode | null;
    context: Record<string, unknown>;
}

export interface RestResponse<T> {
    data: T | null;
    errors: ErrorContext[];
}

/* ------------------------------------------------------------------ */
/* user-api types (bare payloads)                                      */
/* ------------------------------------------------------------------ */

export interface UserInfo {
    id: string;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    userType: string | null;
    picture: string | null;
    partner: boolean;
    superAdmin: boolean;
    readOnly: boolean;
    account: unknown | null;
    modificationDate: string;
    domain: string | null;
    accountId: string | null;
    properties: Record<string, unknown>;
    csrfToken: string;
}

export interface CurrentPredictPendoUser extends UserInfo {
    properties: {
        subId: string;
        appId: string;
        pendoUserId: string;
    };
}

export interface PredictPendoAnalysis {
    appId: string;
    analysisId: string;
}

export type AvailableAnalysesResponse = PredictPendoAnalysis[];

export type LogoutResponse = boolean;

/* ------------------------------------------------------------------ */
/* gateway types (wrapped in RestResponse)                             */
/* ------------------------------------------------------------------ */

export interface Field {
    table: string | null;
    tableLabel: string | null;
    field: string | null;
    label: string | null;
    type: string | null;
    columnType: string | null;
    system: boolean;
    sources: string[] | null;
}

export interface UseCase {
    id: string | null;
    label: string | null;
}

export interface AnalysisFeatureCategoryScale {
    id: string | null;
    label: string | null;
    fromValue: number | null;
    toValue: number | null;
}

export interface AnalysisFeatureCategory {
    id: string;
    label: string | null;
    description: string | null;
    type: string;
    scope: string;
    scopeKey: string | null;
    system: boolean;
    scale: AnalysisFeatureCategoryScale[] | null;
    useCases: UseCase[] | null;
    deleted: boolean;
    featureUsageCount: number;
    analysisUsageCount: number;
}

export interface MetricDb {
    id: string | null;
    description: string | null;
}

export interface AnalysisDto {
    id: string;
    creationDate: string;
    modificationDate: string;
    projectId: string;
    accountId: string;
    user: string;
    name: string;
    decisionBaseId: string | null;
    metricId: string | null;
    metricDb: MetricDb | null;
    metricDefinitionId: string | null;
    datasetPostProcessId: string | null;
    analysisHistogramId: string | null;
    analysisModelId: string | null;
    analysisEnrichmentId: string | null;
    generativeAnalysisId: string | null;
    customerQualificationId: string | null;
    excludedFeatures: string[] | null;
    addedFeatures: string[] | null;
    customColumns: string[] | null;
    explainBlacklistColumns: string[] | null;
    customization: Record<string, unknown> | null;
    properties: Record<string, unknown> | null;
    featureCategories: Record<string, string>;
    explainLabelMapping: Record<string, string>;
    analysisType: string;
    delete: boolean;
}

export interface AnalysisBundleResponse {
    analysis: AnalysisDto | null;
    categoryMapper: AnalysisFeatureCategory[] | null;
    mapper: Record<string, Field> | null;
    inverseMapper: Record<string, string> | null;
    userProperties: Record<string, unknown> | null;
    accountProperties: Record<string, unknown> | null;
}

export interface CategoryScale {
    scaleId: string | null;
    scaleValue: string | null;
}

export interface CategoryField {
    categoryId: string | null;
    categoryLabel: string | null;
    pendoFieldName: string | null;
    scales: CategoryScale[] | null;
}

export interface PendoFieldPrefixResponse {
    pendoFieldPrefix: string | null;
    categoryFields: CategoryField[];
}

/* ------------------------------------------------------------------ */
/* user-api endpoints (called directly, bare payloads)                 */
/* ------------------------------------------------------------------ */

// Pre-login discovery: the provisioned {appId, analysisId} pairs for the sub.
export function getAvailableAnalyses (pendoSubscriptionId: string): Promise<AvailableAnalysesResponse> {
    return http
        .get<AvailableAnalysesResponse>(`${PREDICT_API_BASE}/user/predict-pendo/available-analyses`, {
            params: { pendoSubscriptionId },
            headers: predictCsrfHeaders(),
            withCredentials: true,
        })
        .then((response) => response.data);
}

// The last successful login's context, cached so an expired session can be
// re-established without the caller re-supplying subscription/app ids.
let lastPredictLoginContext: { pendoSubscriptionId: string; pendoAppId: string } | null = null;

// Shared in-flight re-login. Concurrent 401s dedupe onto this single promise so
// we never fire more than one login at a time.
let pendingRelogin: Promise<void> | null = null;
// Bumped on explicit logout. In-flight logins only persist context when they
// resolve against the same version they started with.
let predictAuthContextVersion = 0;

// Establishes the Forwrd session; sets predict.pendo.jwt + predict.csrf cookies.
export function predictPendoLogin (pendoSubscriptionId: string, pendoAppId: string): Promise<UserInfo> {
    const authContextVersionAtStart = predictAuthContextVersion;

    return http
        .post<UserInfo>(
            `${PREDICT_API_BASE}/user/login/predict-pendo`,
            null,
            { params: { pendoSubscriptionId, pendoAppId }, headers: predictCsrfHeaders(), withCredentials: true },
        )
        .then((response) => {
            cachedPredictCsrfToken = response.data?.csrfToken || null;
            // Remember the context so a 401 on a later data call can silently
            // re-establish the session (see withPredictReloginRetry).
            if (authContextVersionAtStart === predictAuthContextVersion) {
                lastPredictLoginContext = { pendoSubscriptionId, pendoAppId };
            }

            return response.data;
        });
}

/* ------------------------------------------------------------------ */
/* Silent re-login on 401                                              */
/* ------------------------------------------------------------------ */

export function isPredictUnauthorized (error: unknown): boolean {
    return (error as { response?: { status?: number } })?.response?.status === 401;
}

function reloginPredict (): Promise<void> {
    if (!lastPredictLoginContext) {
        return Promise.reject(new Error('Cannot re-login to Predict: no cached login context'));
    }

    if (!pendingRelogin) {
        const { pendoSubscriptionId, pendoAppId } = lastPredictLoginContext;

        pendingRelogin = predictPendoLogin(pendoSubscriptionId, pendoAppId)
            .then(() => undefined)
            .finally(() => {
                pendingRelogin = null;
            });
    }

    return pendingRelogin;
}

// Runs a Predict request and, on a single 401, re-logs-in (once, deduped) and
// retries the request exactly once. A 401 on the retry — or any other error —
// propagates, so this can never loop.
export async function withPredictReloginRetry<T> (request: () => Promise<T>): Promise<T> {
    try {
        return await request();
    } catch (error) {
        if (error instanceof PredictCsrfTokenError && lastPredictLoginContext) {
            await reloginPredict();

            return request();
        }

        if (!isPredictUnauthorized(error) || !lastPredictLoginContext) {
            throw error;
        }

        try {
            await reloginPredict();
        } catch (reloginError) {
            const wrappedError = new Error('Predict re-login failed after 401', { cause: reloginError });
            (wrappedError as Error & { originalUnauthorizedError?: unknown }).originalUnauthorizedError = error;

            throw wrappedError;
        }

        return request();
    }
}

// Returns the current authenticated Predict Light session/user.
export function getCurrentPredictPendoUser (): Promise<CurrentPredictPendoUser> {
    return withPredictReloginRetry(() =>
        http
            .get<CurrentPredictPendoUser>(`${PREDICT_API_BASE}/user/predict-pendo/current`, {
                headers: predictCsrfHeaders({ requireToken: true }),
                withCredentials: true,
            })
            .then((response) => response.data),
    );
}

// Deletes the Redis session and clears the cookie.
export function predictPendoLogout (): Promise<LogoutResponse> {
    return http
        .post<LogoutResponse>(`${PREDICT_API_BASE}/user/logout/predict-pendo`, null, {
            headers: predictCsrfHeaders({ requireToken: true }),
            withCredentials: true,
        })
        .then((response) => response.data)
        .finally(() => {
            predictAuthContextVersion += 1;
            cachedPredictCsrfToken = null;
            // Explicit logout must also clear the cached re-login context. Otherwise a
            // later 401 can silently re-authenticate with stale subscription/app ids.
            lastPredictLoginContext = null;
            pendingRelogin = null;
        });
}

/* ------------------------------------------------------------------ */
/* experiment-msvc endpoints (via gateway, wrapped in RestResponse)    */
/* ------------------------------------------------------------------ */

// Fetches the analysis and wraps it into the bundle shape the callers expect.
export function getAnalysisBundle (analysisId: string): Promise<AnalysisBundleResponse | null> {
    const url = `${PREDICT_API_BASE}/predict-pendo/analysis/${analysisId}`;

    // This endpoint returns the bare AnalysisDto (not a RestResponse bundle),
    // so wrap it back into the bundle shape the callers expect.
    return withPredictReloginRetry(() =>
        http
            .get<AnalysisDto>(url, { headers: predictCsrfHeaders({ requireToken: true }), withCredentials: true })
            .then((response) => ({
                analysis: response.data ?? null,
                categoryMapper: null,
                mapper: null,
                inverseMapper: null,
                userProperties: null,
                accountProperties: null,
            })),
    );
}

// Pendo field prefix + per-category field mapping for the analysis' model.
export function getPendoFieldPrefix (analysisId: string): Promise<PendoFieldPrefixResponse | null> {
    return withPredictReloginRetry(() =>
        http
            .get<RestResponse<PendoFieldPrefixResponse>>(
                `${PREDICT_API_BASE}/predict-pendo/analysis/${analysisId}/pendo-field-prefix`,
                { headers: predictCsrfHeaders({ requireToken: true }), withCredentials: true },
            )
            .then((response) => {
                const { errors, data } = response.data;

                if (errors?.length) {
                    throw new Error(`Predict field-prefix request failed: ${JSON.stringify(errors)}`);
                }

                return data;
            }),
    );
}
